import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/ui/lib/utils'

export interface SelectOption<T extends string = string> {
  value: T
  label: ReactNode
  /** An optional leading swatch (e.g. the hue-picker's colour dot) shown before `label`. */
  swatch?: ReactNode
  disabled?: boolean
}

export interface SelectProps<T extends string = string> {
  value: T | null
  onValueChange: (value: T) => void
  options: SelectOption<T>[]
  placeholder?: string
  id?: string
  ariaLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Station Kit's plain dossier select (spec 007 PR1/T2.3) — replaces the
 * retired shadcn `Select` primitive. This is the AG-7/AG-3 fix, built in from
 * the start rather than retrofitted: the options list is portaled to
 * `document.body` via `createPortal` and rendered on a solid, opaque
 * `--paper` background with a hairline border and a `z-index` above page
 * content, so a clipping/overflow ancestor (a scroll pane, a `overflow:
 * hidden` card, …) can never make it transparent or invisible. Every later
 * dropdown in this codebase (the hue picker, the model select) should reuse
 * this component rather than re-solving the clipping problem.
 *
 * A hand-rolled combobox/listbox (no new dependency): the trigger carries
 * `role="combobox"`, the portaled panel `role="listbox"`, each row
 * `role="option"` — keyboard-operable (Up/Down to move, Enter/Space to
 * pick, Escape to close) and closable on an outside click.
 */
export function Select<T extends string = string>({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  id,
  ariaLabel,
  disabled,
  className,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{
    left: number
    width: number
    maxHeight: number
    top?: number
    bottom?: number
  } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const listboxId = useId()

  const selectedIndex = options.findIndex((option) => option.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined

  // Position the portaled panel against the trigger's current on-screen rect
  // every time it opens (and on scroll/resize while open), so it always
  // tracks the trigger even though it renders as a body-level sibling rather
  // than a DOM descendant.
  useEffect(() => {
    if (!open) return

    function updatePosition() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      // Keep the panel fully inside the viewport: open downward from the
      // trigger's bottom, but flip upward when there's more room above than
      // below (a trigger low on the page). Either way the panel's max-height is
      // clamped to the available space so its options never fall off-screen and
      // become unclickable (they stay reachable via the list's own scroll).
      const margin = 8
      const spaceBelow = window.innerHeight - rect.bottom - margin
      const spaceAbove = rect.top - margin
      const cap = 256
      if (spaceBelow < Math.min(cap, spaceAbove) && spaceAbove > spaceBelow) {
        setPosition({
          bottom: window.innerHeight - rect.top,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(0, Math.min(cap, spaceAbove)),
        })
      } else {
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(0, Math.min(cap, spaceBelow)),
        })
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open])

  // Close on an outside click/tap and on Escape.
  useEffect(() => {
    if (!open) return

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || listboxRef.current?.contains(target)) return
      setOpen(false)
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // Capture phase + stopPropagation: an open list consumes Esc, so an enclosing
        // Dialog's document-level Esc listener never sees it and stays open.
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [open])

  function pick(option: SelectOption<T>) {
    if (option.disabled) return
    onValueChange(option.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setOpen(true)
    }
  }

  function handleOptionKeyDown(event: ReactKeyboardEvent<HTMLLIElement>, index: number) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      const nextEl = listboxRef.current?.querySelectorAll<HTMLLIElement>('[role="option"]')[(index + 1) % options.length]
      nextEl?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      const prevIndex = (index - 1 + options.length) % options.length
      const prevEl = listboxRef.current?.querySelectorAll<HTMLLIElement>('[role="option"]')[prevIndex]
      prevEl?.focus()
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      pick(options[index])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 border border-current/25 bg-transparent px-[10px] py-[8px] font-mono text-sm text-current focus:border-accent-fill focus:outline-none disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
      >
        <span className="flex flex-1 items-center gap-2 truncate">
          {selected?.swatch}
          <span className="truncate">{selected?.label ?? placeholder}</span>
        </span>
        <span aria-hidden="true">▾</span>
      </button>

      {open &&
        createPortal(
          <ul
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            style={{
              position: 'fixed',
              top: position?.top,
              bottom: position?.bottom,
              left: position?.left ?? 0,
              width: position?.width,
              maxHeight: position?.maxHeight,
              // Belt-and-suspenders alongside the `bg-paper` utility below:
              // an explicit inline background guarantees an opaque panel even
              // in an environment that can't resolve Tailwind's generated
              // stylesheet (this is also what the regression test below
              // reads via `getComputedStyle`, since it references the same
              // `--paper` token rather than a second, driftable value).
              backgroundColor: 'var(--paper)',
            }}
            className="z-50 overflow-y-auto border border-ink-25 bg-paper font-mono text-sm text-ink shadow-none"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
                tabIndex={option.disabled ? -1 : 0}
                onClick={() => pick(option)}
                onKeyDown={(event) => handleOptionKeyDown(event, index)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 truncate px-3 py-2 hover:bg-accent-wash focus:bg-accent-wash focus:outline-none',
                  option.value === value && 'bg-accent-wash',
                  option.disabled && 'pointer-events-none opacity-50',
                )}
              >
                {option.swatch}
                <span className="truncate">{option.label}</span>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </>
  )
}
